import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useReportError, extractErrorInfo } from '@features/support';
import { productService } from '../services/productService';
import type { Product, ProductListParams } from '../types/product.types';

export function useProducts(params?: ProductListParams) {
  // Clean params - remove undefined values to ensure proper cache keys
  const cleanParams = params ? {
    page: params.page || 1,
    ...(params.limit && { limit: params.limit }),
    ...(params.name && { name: params.name }),
    ...(params.code && { code: params.code }),
    ...(params.category && { category: params.category }),
    ...(params.isActive !== undefined && { isActive: params.isActive }),
  } : { page: 1 };

  console.log('🔑 useProducts - Query Key:', ['products', cleanParams]);

  return useQuery({
    queryKey: ['products', cleanParams],
    queryFn: () => {
      console.log('🔄 useProducts - Fetching with params:', cleanParams);
      return productService.getProducts(cleanParams);
    },
    staleTime: 0, // Always refetch to get fresh data
    gcTime: 5 * 60 * 1000, // 5 minutes cache time
    placeholderData: keepPreviousData,
  });
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: ['product', id],
    queryFn: () => productService.getProductById(id),
    enabled: !!id,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  // Asistente de soporte: explica el fallo del alta en vez del error crudo.
  const reportError = useReportError();

  return useMutation({
    mutationFn: (product: Partial<Product>) => {
      console.log('🔵 useCreateProduct - mutationFn called with:', product);
      return productService.createProduct(product);
    },
    onSuccess: (data) => {
      console.log('✅ useCreateProduct - Success:', data);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
    onError: (error: any) => {
      console.error('❌ useCreateProduct - Error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        fullError: error,
      });
      reportError({ title: 'Error al crear producto', screen: 'crear producto', ...extractErrorInfo(error) });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Product> }) =>
      productService.updateProduct(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productService.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
  });
}
